// KIADÁSOK feature — query-kulcs gyár, a lista-szűrő típusa és a kategória-fa megjelenítési segédei.
// A hívások a tipizált ts-rest kliensen (`api.expenses.*`) mennek; a kulcsok itt élnek, hogy a
// cache-invalidálás egy helyen legyen. A szerződés forrás-igazsága a `@atlas/contracts`.

import type { ExpenseCategoryName, ExpenseCategoryRow } from '@atlas/contracts';

/** A „kategorizálatlan" szűrő sentinelje — a BFF ugyanezt a szót ismeri (a Radix Select nem enged üres value-t). */
export const UNCATEGORIZED = 'none';

/**
 * A KIADÁS-oldali kibocsátók: a két cégünk, amelyik bejövő számlát kaphat.
 *
 * A `none` SZÁNDÉKOSAN nincs köztük: az a számla-oldali számlatömb-prefix hiányát jelenti, nem külön céget
 * (a `none` és a `MOR` UGYANAZ az adózó — MORGENS Design, 23964710). A kiadás-oldalon tehát két valódi
 * adózó van, és a lista ezt a kettőt ismeri.
 */
export const EXPENSE_ISSUERS = ['MOR', 'MDIG'] as const;

export interface ExpenseListFilters {
  month: string;
  /** Kibocsátó-szűrő (`all` · `MOR` · `MDIG`) — a query-kulcs része. */
  issuerKey: string;
  categoryId: string;
  source: string;
  /** `hidden` (alap) · `only` · `all` — az elvetett sorok kezelése. */
  dismissed: string;
  q: string;
}

export const expenseKeys = {
  all: ['expenses'] as const,
  lists: () => ['expenses', 'list'] as const,
  list: (filters: ExpenseListFilters) => ['expenses', 'list', filters] as const,
  /** Az ÖSSZEGZÉS fül kulcsa: ugyanaz a végpont, de szűrő és sorok nélkül (`limit: 1`) — saját bejegyzés,
   *  hogy a kiadás-lista szűrő-váltásai ne írják felül, és fordítva. */
  summary: (month: string) => ['expenses', 'summary', month] as const,
  categories: () => ['expenses', 'categories'] as const,
  /** Az Összegzés kiadás-grafikonjai (12 hónapos trend + a hónap bontásai). */
  analytics: (month: string, filters: Record<string, unknown> = {}) =>
    ['expenses', 'analytics', month, filters] as const,
};

/** A kétnyelvű név a felület nyelvén; `en` hiányában a magyarra esik vissza (a `hu` mindig kötelező). */
export function categoryLabel(
  name: ExpenseCategoryName | null | undefined,
  locale: string,
): string {
  if (!name) return '';
  if (locale.toLowerCase().startsWith('en') && name.en) return name.en;
  return name.hu;
}

/**
 * Egy kategória MÉLYSÉGE a fában: 0 = főkategória · 1 = alkategória · 2 = tétel.
 *
 * ⚠️ CIKLUS-VÉDELEM: a lánc-járás látott halmazt vezet. Az írás-út tiltja a körkörös fát, de egy elrontott
 * sor különben a renderelést fagyasztaná be — a felületen ez fehér lap, nem egy hibás sor.
 */
export function categoryDepth(
  row: ExpenseCategoryRow,
  rows: readonly ExpenseCategoryRow[],
): number {
  const byId = new Map(rows.map((r) => [r.id, r]));
  const seen = new Set<string>([row.id]);
  let depth = 0;
  let cursor = row.parentId;
  while (cursor !== null && !seen.has(cursor)) {
    seen.add(cursor);
    depth += 1;
    cursor = byId.get(cursor)?.parentId ?? null;
  }
  return depth;
}

/**
 * A select-opciók a HÁROM SZINTŰ fából, a BFF sorrendjében (minden csomópont után azonnal a gyerekei).
 *
 * A szintet a `depth` jelöli, és a behúzást a KÖZÖS `SelectItem` rajzolja (szintenként behúzás +
 * vezetővonal). Korábban egy `· ` szöveg-előtag volt a NÉVBEN — az nem behúzás, hanem írásjel: a fa nem
 * látszott ki belőle, és a kiválasztott értékbe (a triggerbe) is beleragadt.
 */
export function categoryOptions(
  rows: readonly ExpenseCategoryRow[],
  locale: string,
): { value: string; label: string; depth: number }[] {
  return rows.map((row) => ({
    value: row.id,
    label: categoryLabel(row.name, locale),
    depth: categoryDepth(row, rows),
  }));
}

/**
 * A BESOROLÁSHOZ választható opciók: KIZÁRÓLAG a LEVÉL-kategóriák, az águkkal mint halvány második sorral.
 *
 * MIÉRT CSAK A LEVÉL (user-döntés): egy kiadás a fa VÉGÉRE tartozik, nem egy gyűjtő-csomópontra. Eddig a
 * select mind a 122 csomópontot felkínálta, tehát egy főkategóriára is be lehetett sorolni — a kimutatás
 * pedig a gyökérre összegez, így az ilyen sor „megvolt", de a részletes bontásból hiányzott.
 *
 * ⚠️ MIÉRT „LEVÉL" ÉS NEM „3. SZINT": a fa nem egyenletesen mély. Élesben mérve NÉGY olyan kategória van,
 * aminek nincs gyereke, de nem a 3. szinten áll (egy üres FŐkategória és három tétel nélküli alkategória) —
 * a „csak 3. szint" szabály ezeket elérhetetlenné tenné. A levél-szabály ráadásul ÖNKARBANTARTÓ: ha valaki
 * később tételt vesz fel egy eddigi levél alá, az magától gyűjtő-csomóponttá válik, és a kód változatlan.
 *
 * A `sublabel` a gyökértől a szülőig tartó ÚT — a keresés erre is illeszt (l. `ComboboxOption`), tehát egy
 * ág nevére gépelve az alatta lévő összes tétel előjön.
 */
export function categoryLeafOptions(
  rows: readonly ExpenseCategoryRow[],
  locale: string,
): { id: string; label: string; sublabel: string }[] {
  const hasChild = new Set(rows.map((r) => r.parentId).filter((id): id is string => id !== null));
  const byId = new Map(rows.map((r) => [r.id, r]));
  return rows
    .filter((row) => !hasChild.has(row.id))
    .map((row) => {
      const path: string[] = [];
      const seen = new Set<string>([row.id]);
      let cursor = row.parentId;
      while (cursor !== null && !seen.has(cursor)) {
        seen.add(cursor);
        const node = byId.get(cursor);
        if (!node) break;
        path.unshift(categoryLabel(node.name, locale));
        cursor = node.parentId;
      }
      return { id: row.id, label: categoryLabel(row.name, locale), sublabel: path.join(' / ') };
    });
}
